import React, { Suspense } from "react"
import { createRoot } from "react-dom/client"
import I18n from "lib/i18n"
import LazyLoad from "components/LazyLoad"
import ConsentGate from "components/ConsentGate"

// This is a no-op intentionally, if something goes wrong we
// just render nothing for now.
const handleTwitterRenderError = () => {}

const LazyTweet = React.lazy(() => import("components/Tweet"))
const LazyInstagram = React.lazy(() => import("components/InstagramEmbed"))
const LazyTiktok = React.lazy(() => import("components/TiktokEmbed"))
const LazyBluesky = React.lazy(() => import("components/BlueskyEmbed"))
const LazyThread = React.lazy(() => import("components/ThreadEmbed"))
const LazyYouTube = React.lazy(() => import("components/YouTubeEmbed"))
const LazyVimeo = React.lazy(() => import("components/VimeoEmbed"))

function Tweet(props) {
  return (
    <Suspense fallback={null}>
      <LazyTweet {...props} />
    </Suspense>
  )
}

function Instagram(props) {
  return (
    <Suspense fallback={null}>
      <LazyInstagram {...props} />
    </Suspense>
  )
}

function Tiktok(props) {
  return (
    <Suspense fallback={null}>
      <LazyTiktok {...props} />
    </Suspense>
  )
}

function Bluesky(props) {
  return (
    <Suspense fallback={null}>
      <LazyBluesky {...props} />
    </Suspense>
  )
}

function Thread(props) {
  return (
    <Suspense fallback={null}>
      <LazyThread {...props} />
    </Suspense>
  )
}

function YouTube(props) {
  return (
    <LazyLoad>
      <ConsentGate
        vendorId="youtube"
        notice={I18n.t("consent_gate.label.notice.youtube")}
        buttonText={I18n.t("consent_gate.button.load.youtube")}
      >
        <Suspense fallback={null}>
          <LazyYouTube {...props} />
        </Suspense>
      </ConsentGate>
    </LazyLoad>
  )
}

function Vimeo(props) {
  return (
    <LazyLoad>
      <Suspense fallback={null}>
        <LazyVimeo {...props} />
      </Suspense>
    </LazyLoad>
  )
}

const rehydrateComponents = (container, component, componentName, renderError) => {
  const nodes = Array.from(container.querySelectorAll(`[data-react-class=${componentName}]`))
  nodes.forEach((node) => {
    const props = JSON.parse(node.dataset.reactProps)
    const socialMediaComponent = React.createElement(component, { ...props, renderError })
    const root = createRoot(node)
    root.render(socialMediaComponent)
  })
}

// Rehydrate dynamic content in a given content body
//
// Usage:
//
// import initializeContentBody from "lib/content_body";
//
// const node = document.querySelector("div.something");
// initializeContentBody(node);
//
export default (node) => {
  rehydrateComponents(node, Tweet, "Tweet", handleTwitterRenderError)
  rehydrateComponents(node, Instagram, "InstagramEmbed")
  rehydrateComponents(node, Thread, "ThreadEmbed")
  rehydrateComponents(node, Tiktok, "TiktokEmbed")
  rehydrateComponents(node, Bluesky, "BlueskyEmbed")
  rehydrateComponents(node, YouTube, "YouTubeEmbed")
  rehydrateComponents(node, Vimeo, "VimeoEmbed")
}
